import React, { useState } from 'react';
import { Modal, Form, Input, Button, message, Typography } from 'antd';
import { Import } from 'lucide-react';
import useProductGroupOpenApi from 'apiClients/useProductGroupOpenApiClient';

const { TextArea } = Input;
const { Text } = Typography;

interface ProductGroupAssignSkuDialogProps {
  visible: boolean;
  onCancel: () => void;
  productGroupId: number;
  onSuccess?: () => void;
}

const ProductGroupAssignSkuDialog: React.FC<ProductGroupAssignSkuDialogProps> = ({
  visible,
  onCancel,
  productGroupId,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { assignProductsBySkus } = useProductGroupOpenApi();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const skusText = values.skus?.trim();
      
      if (!skusText) {
        message.warning('Vänligen ange minst ett artikelnummer');
        return;
      }

      // Split by lines and filter out empty lines
      const skus = skusText
        .split('\n')
        .map((sku: string) => sku.trim())
        .filter((sku: string) => sku.length > 0);

      if (skus.length === 0) {
        message.warning('Vänligen ange minst ett giltligt artikelnummer');
        return;
      }

      setLoading(true);
      
      const result = await assignProductsBySkus(productGroupId, skus);
      
      if (result) {
        message.success(`${skus.length} artikelnummer(s) har tilldelats produktgruppen framgångsrikt`);
        form.resetFields();
        onSuccess?.();
        onCancel();
      } else {
        message.error('Fel vid tilldelning av artikelnummer(s) till produktgruppen');
      }
    } catch (error) {
      console.error('Error assigning artikelnummer:', error);
      message.error('Ett oväntat fel inträffade');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title="Tilldela produkter via artikelnummer"
      open={visible}
      onCancel={handleCancel}
      width={600}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Avbryt
        </Button>,
        <Button
          key="submit" 
          type="primary" 
          loading={loading}
          onClick={handleSubmit}
        >
          Koppla artikelnummer
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        name="assignSkuForm"
      >
        <Form.Item
          name="skus"
          label="Artikelnummer"
          rules={[{ required: true, message: 'Vänligen ange minst ett artikelnummer!' }]}
          extra={
            <Text type="secondary">
              Ange ett artikelnummer per rad. Tomma rader kommer att ignoreras.
            </Text>
          }
        >
          <TextArea
            placeholder={`Ange artikelnummer här, en per rad:\n\n123456\n789012\n345678`}
            rows={8}
            showCount
            maxLength={10000}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ProductGroupAssignSkuDialog;
